import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, Upload, Package, CheckCircle, XCircle, Plus, Info } from 'lucide-react';
import { apiService } from '@/services/api';
import { useProjeto } from '@/contexts/projeto-hook';
import { useI18n } from '@/contexts/i18n-hook';
import { rotuloAreaAtivo, rotuloStatusAtivo } from '@/lib/i18n';
import {
  EVENTO_DOCUMENTOS_MAQUINA,
  TIPOS_DOCUMENTO_MAQUINA,
  resumirDocumentosMaquina,
} from '@/lib/mock-documentos-maquina';
import { ResumoDocumentos } from '@/components/maquinas/resumo-documentos';

interface Apreciacao {
  id: string;
  numero: string;
  titulo: string;
  status: string;
  revisao: number;
  documentos?: Array<{ id: string; nome: string; tipo: string }>;
  _count?: {
    documentos: number;
    riscos: number;
  };
}

interface Ativo {
  id: string;
  area?: string;
  local?: string;
  item?: string;
  idItem?: string;
  equipamento?: string;
  tipo?: string;
  template?: string;
  funcionario?: string;
  dataLevantamento?: string;
  refSismanaAntigo?: string;
  refSismanaNovo?: string;
  refSap?: string;
  tag?: string;
  numeroAprPraxis?: string;
  aprTramitada?: boolean;
  numeroAmt?: string;
  numeroAmtTramitado?: boolean;
  previsaoLevantamento?: string;
  levantamento?: string;
  status?: string;
  observacao?: string;
  createdAt?: string;
  apreciacoes?: Apreciacao[];
}

interface ValoresUnicos {
  areas: string[];
  locais: string[];
  status: string[];
  tipos: string[];
}

export function Dashboard() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { projeto } = useProjeto();
  const [ativos, setAtivos] = useState<Ativo[]>([]);
  const [valoresUnicos, setValoresUnicos] = useState<ValoresUnicos>({
    areas: [],
    locais: [],
    status: [],
    tipos: [],
  });
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({
    busca: '',
    area: '',
    local: '',
    status: '',
    tipo: '',
    comApreciacao: false,
  });
  const [paginacao, setPaginacao] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });
  const [importando, setImportando] = useState(false);
  // Filtros do acervo documental. Vivem separados de `filtros` de propósito: a
  // origem deles é o protótipo local, não a API — misturar os dois mandaria
  // parâmetro desconhecido para `/ativos` e recarregaria a lista à toa.
  const [filtrosDocumentos, setFiltrosDocumentos] = useState({ tipoPendente: '', situacao: '' });
  const [versaoDocumentos, setVersaoDocumentos] = useState(0);

  const carregarAtivos = async () => {
    if (!projeto) return;
    
    try {
      setLoading(true);
      const response = await apiService.listarAtivos({
        projetoId: projeto.id,
        ...filtros,
        page: paginacao.page,
        limit: paginacao.limit,
      });

      if (response.success && response.data) {
        const ativosData = Array.isArray(response.data) ? response.data : [];

        // Debug: verificar se apreciações estão sendo retornadas
        const ativoComApreciacoes = ativosData.find((a: Ativo) => a.apreciacoes && a.apreciacoes.length > 0);
        if (ativoComApreciacoes) {
          console.log(`[Dashboard] Ativo ${ativoComApreciacoes.id} tem ${ativoComApreciacoes.apreciacoes?.length} apreciações`, ativoComApreciacoes.apreciacoes);
        } else {
          console.log(`[Dashboard] Nenhum ativo com apreciações encontrado. Total de ativos: ${ativosData.length}`);
          // Verificar se algum ativo tem o campo apreciacoes
          const primeiroAtivo = ativosData[0];
          if (primeiroAtivo) {
            console.log(`[Dashboard] Primeiro ativo:`, {
              id: primeiroAtivo.id,
              equipamento: primeiroAtivo.equipamento,
              temApreciacoes: !!primeiroAtivo.apreciacoes,
              apreciacoes: primeiroAtivo.apreciacoes
            });
          }
        }

        setAtivos(ativosData);
        if (response.pagination) {
          setPaginacao(prev => ({
            ...prev,
            total: response.pagination!.total,
            totalPages: response.pagination!.totalPages,
          }));
        }
      }
    } catch (error) {
      console.error('Erro ao carregar ativos:', error);
    } finally {
      setLoading(false);
    }
  };

  const carregarValoresUnicos = async () => {
    try {
      const response = await apiService.obterValoresUnicosAtivos();
      if (response.success && response.data) {
        setValoresUnicos(response.data as ValoresUnicos);
      }
    } catch (error) {
      console.error('Erro ao carregar valores únicos:', error);
    }
  };

  useEffect(() => {
    if (projeto) {
      carregarAtivos();
      carregarValoresUnicos();
    }
  }, [projeto, filtros, paginacao.page, paginacao.limit]);

  useEffect(() => {
    const aoMudar = () => setVersaoDocumentos((v) => v + 1);
    window.addEventListener(EVENTO_DOCUMENTOS_MAQUINA, aoMudar);
    return () => window.removeEventListener(EVENTO_DOCUMENTOS_MAQUINA, aoMudar);
  }, []);

  const resumoPorAtivo = useMemo(() => {
    const mapa = new Map<string, ReturnType<typeof resumirDocumentosMaquina>>();
    ativos.forEach((ativo) => mapa.set(ativo.id, resumirDocumentosMaquina(ativo.id)));
    return mapa;
  }, [ativos, versaoDocumentos]);

  const filtroDocumentoAtivo = Boolean(filtrosDocumentos.tipoPendente || filtrosDocumentos.situacao);

  /**
   * O recorte por documento acontece aqui, sobre a página que a API devolveu —
   * o acervo ainda não existe no banco, então não há como pedir isso ao
   * servidor. É a limitação conhecida do protótipo, e a tela avisa.
   */
  const ativosVisiveis = useMemo(() => {
    if (!filtroDocumentoAtivo) return ativos;
    return ativos.filter((ativo) => {
      const resumo = resumoPorAtivo.get(ativo.id);
      if (!resumo) return false;

      if (filtrosDocumentos.tipoPendente && !resumo.tiposPendentes.includes(filtrosDocumentos.tipoPendente)) {
        return false;
      }

      const emAberto = resumo.pendentes + resumo.emAnalise;
      if (filtrosDocumentos.situacao === 'com_pendencia' && emAberto === 0) return false;
      if (filtrosDocumentos.situacao === 'sem_pendencia' && emAberto > 0) return false;
      if (filtrosDocumentos.situacao === 'atrasado' && resumo.atrasados === 0) return false;

      return true;
    });
  }, [ativos, filtroDocumentoAtivo, filtrosDocumentos, resumoPorAtivo]);

  const abrirMaquina = (ativo: Ativo) => {
    // A linha viaja junto para a ficha aproveitar as apreciações já carregadas.
    navigate(`/maquinas/${ativo.id}`, { state: { maquina: ativo } });
  };

  const handleImportar = async () => {
    if (!projeto) {
      alert(t('dashboard.importacao.semProjeto'));
      return;
    }

    try {
      setImportando(true);
      const filePath = 'Curva S.xlsx';
      const response = await apiService.importarAtivos(filePath, projeto.id);

      if (response.success) {
        alert(t('dashboard.importacao.sucesso', { mensagem: response.message ?? '' }));
        carregarAtivos();
        carregarValoresUnicos();
      } else {
        alert(t('dashboard.importacao.erro', { mensagem: response.message ?? '' }));
      }
    } catch (error: any) {
      console.error('Erro ao importar:', error);
      
      // Verificar se o erro é porque o projeto não existe
      const errorMessage = error?.message || error?.toString() || '';
      if (errorMessage.includes('não encontrado') || errorMessage.includes('not found') || errorMessage.includes('Projeto')) {
        alert(t('dashboard.importacao.projetoInexistente'));
        // Limpar projeto do localStorage e redirecionar para seleção
        localStorage.removeItem('projetoAtivo');
        navigate('/projetos');
        return;
      }
      
      alert(t('dashboard.importacao.erroGenerico'));
    } finally {
      setImportando(false);
    }
  };

  const handleFiltroChange = (campo: string, valor: string) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
    setPaginacao(prev => ({ ...prev, page: 1 }));
  };

  const limparFiltros = () => {
    setFiltros({
      busca: '',
      area: '',
      local: '',
      status: '',
      tipo: '',
      comApreciacao: false,
    });
    setFiltrosDocumentos({ tipoPendente: '', situacao: '' });
    setPaginacao(prev => ({ ...prev, page: 1 }));
  };

  return (
    <div className="flex flex-col md:h-full">
      {/* Header */}
      {/* Header */}
      <div className="flex-shrink-0 flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4 md:gap-0">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-schmersal-blue dark:text-white">{t('dashboard.titulo')}</h1>
          <p className="mt-1 text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
            {t('dashboard.subtitulo')}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
          <Link
            to="/maquinas/nova"
            className="w-full sm:w-auto inline-flex items-center justify-center px-5 py-2.5 border border-schmersal-blue
                     text-sm font-semibold rounded-lg shadow-md
                     text-schmersal-blue bg-white dark:bg-schmersal-gray-darker dark:text-white dark:border-schmersal-blue
                     hover:bg-schmersal-blue hover:text-white dark:hover:bg-schmersal-blue
                     focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-schmersal-blue
                     transition-all duration-200"
          >
            <Plus className="h-5 w-5 mr-2" />
            {t('dashboard.acoes.novaMaquina')}
          </Link>
          <button
            onClick={handleImportar}
            disabled={importando}
            className="w-full sm:w-auto inline-flex items-center justify-center px-5 py-2.5 border-2 border-transparent
                     text-sm font-semibold rounded-lg shadow-md
                     text-white bg-schmersal-blue dark:bg-schmersal-blue-light
                     hover:bg-schmersal-blue-dark dark:hover:bg-schmersal-blue
                     focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-schmersal-blue
                     disabled:opacity-50 disabled:cursor-not-allowed
                     transition-all duration-200"
          >
            <Upload className="h-5 w-5 mr-2" />
            {importando ? t('dashboard.acoes.importando') : t('dashboard.acoes.importar')}
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex-shrink-0 bg-white dark:bg-schmersal-gray-darker shadow-sm rounded-lg p-6 mb-6 border border-rg-line dark:border-schmersal-gray-dark">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-schmersal-blue dark:text-white">{t('dashboard.filtros.titulo')}</h3>
          <button
            onClick={limparFiltros}
            className="text-sm font-medium text-schmersal-blue dark:text-schmersal-yellow hover:text-schmersal-blue-dark dark:hover:text-schmersal-yellow-light
                     underline decoration-2 underline-offset-2"
          >
            {t('dashboard.filtros.limpar')}
          </button>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-6">
          <div>
            <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white mb-2">
              {t('dashboard.filtros.busca')}
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-schmersal-gray-medium dark:text-schmersal-gray-light" />
              <input
                type="text"
                value={filtros.busca}
                onChange={(e) => handleFiltroChange('busca', e.target.value)}
                placeholder={t('dashboard.filtros.buscaPlaceholder')}
                className="pl-10 block w-full border border-rg-line dark:border-schmersal-gray-dark rounded-lg 
                         shadow-sm focus:ring-2 focus:ring-schmersal-blue focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-schmersal-gray-dark dark:text-white
                         placeholder-schmersal-gray-medium dark:placeholder-schmersal-gray-medium
                         sm:text-sm py-2.5 transition-all duration-200"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white mb-2">
              {t('comum.rotulo.area')}
            </label>
            <select
              value={filtros.area}
              onChange={(e) => handleFiltroChange('area', e.target.value)}
              className="block w-full border border-rg-line dark:border-schmersal-gray-dark rounded-lg shadow-sm 
                       focus:ring-2 focus:ring-schmersal-blue focus:border-schmersal-blue 
                       bg-white dark:bg-schmersal-gray-darkest text-schmersal-gray-dark dark:text-white
                       sm:text-sm py-2.5 transition-all duration-200"
            >
              <option value="">{t('comum.rotulo.todas')}</option>
              {valoresUnicos.areas.map((area) => (
                <option key={area} value={area}>
                  {rotuloAreaAtivo(t, area)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white mb-2">
              {t('comum.rotulo.local')}
            </label>
            <select
              value={filtros.local}
              onChange={(e) => handleFiltroChange('local', e.target.value)}
              className="block w-full border border-rg-line dark:border-schmersal-gray-dark rounded-lg shadow-sm 
                       focus:ring-2 focus:ring-schmersal-blue focus:border-schmersal-blue 
                       bg-white dark:bg-schmersal-gray-darkest text-schmersal-gray-dark dark:text-white
                       sm:text-sm py-2.5 transition-all duration-200"
            >
              <option value="">{t('comum.rotulo.todos')}</option>
              {valoresUnicos.locais.map((local) => (
                <option key={local} value={local}>
                  {local}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white mb-2">
              {t('comum.rotulo.status')}
            </label>
            <select
              value={filtros.status}
              onChange={(e) => handleFiltroChange('status', e.target.value)}
              className="block w-full border border-rg-line dark:border-schmersal-gray-dark rounded-lg shadow-sm 
                       focus:ring-2 focus:ring-schmersal-blue focus:border-schmersal-blue 
                       bg-white dark:bg-schmersal-gray-darkest text-schmersal-gray-dark dark:text-white
                       sm:text-sm py-2.5 transition-all duration-200"
            >
              <option value="">{t('comum.rotulo.todos')}</option>
              {valoresUnicos.status.map((status) => (
                <option key={status} value={status}>
                  {rotuloStatusAtivo(t, status)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white mb-2">
              {t('comum.rotulo.tipo')}
            </label>
            <select
              value={filtros.tipo}
              onChange={(e) => handleFiltroChange('tipo', e.target.value)}
              className="block w-full border border-rg-line dark:border-schmersal-gray-dark rounded-lg shadow-sm 
                       focus:ring-2 focus:ring-schmersal-blue focus:border-schmersal-blue 
                       bg-white dark:bg-schmersal-gray-darkest text-schmersal-gray-dark dark:text-white
                       sm:text-sm py-2.5 transition-all duration-200"
            >
              <option value="">{t('comum.rotulo.todos')}</option>
              {valoresUnicos.tipos.map((tipo) => (
                <option key={tipo} value={tipo}>
                  {tipo}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white mb-2">
              {t('documentos.filtro.documentoPendente')}
            </label>
            <select
              value={filtrosDocumentos.tipoPendente}
              onChange={(e) => setFiltrosDocumentos(prev => ({ ...prev, tipoPendente: e.target.value }))}
              className="block w-full border border-rg-line dark:border-schmersal-gray-dark rounded-lg shadow-sm
                       focus:ring-2 focus:ring-schmersal-blue focus:border-schmersal-blue
                       bg-white dark:bg-schmersal-gray-darkest text-schmersal-gray-dark dark:text-white
                       sm:text-sm py-2.5 transition-all duration-200"
            >
              <option value="">{t('documentos.filtro.documentoPendenteTodos')}</option>
              {TIPOS_DOCUMENTO_MAQUINA.map((tipo) => (
                <option key={tipo} value={tipo}>
                  {t(`documentos.tipo.${tipo}`)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white mb-2">
              {t('documentos.filtro.situacao')}
            </label>
            <select
              value={filtrosDocumentos.situacao}
              onChange={(e) => setFiltrosDocumentos(prev => ({ ...prev, situacao: e.target.value }))}
              className="block w-full border border-rg-line dark:border-schmersal-gray-dark rounded-lg shadow-sm
                       focus:ring-2 focus:ring-schmersal-blue focus:border-schmersal-blue
                       bg-white dark:bg-schmersal-gray-darkest text-schmersal-gray-dark dark:text-white
                       sm:text-sm py-2.5 transition-all duration-200"
            >
              <option value="">{t('documentos.filtro.situacaoTodas')}</option>
              <option value="com_pendencia">{t('documentos.filtro.situacaoComPendencia')}</option>
              <option value="sem_pendencia">{t('documentos.filtro.situacaoSemPendencia')}</option>
              <option value="atrasado">{t('documentos.filtro.situacaoAtrasado')}</option>
            </select>
          </div>
          <div className="flex items-center">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={filtros.comApreciacao}
                onChange={(e) => {
                  setFiltros(prev => ({ ...prev, comApreciacao: e.target.checked }));
                  setPaginacao(prev => ({ ...prev, page: 1 }));
                }}
                className="w-5 h-5 text-schmersal-blue border border-rg-line dark:border-schmersal-gray-dark rounded 
                         focus:ring-2 focus:ring-schmersal-blue focus:ring-offset-2 
                         bg-white dark:bg-schmersal-gray-darkest
                         transition-all duration-200"
              />
              <span className="ml-3 text-sm font-semibold text-schmersal-gray-dark dark:text-white">
                {t('dashboard.filtros.apenasComApreciacoes')}
              </span>
            </label>
          </div>
        </div>
        {filtroDocumentoAtivo && (
          <p className="mt-4 flex items-start gap-2 text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
            <Info className="h-4 w-4 flex-none mt-px" />
            {t('documentos.filtro.aviso')}
          </p>
        )}
      </div>

      {/* Tabela de Ativos */}
      <div className="flex-1 flex flex-col bg-white dark:bg-schmersal-gray-darker shadow-sm rounded-lg overflow-hidden min-h-[400px] md:min-h-0 border border-rg-line dark:border-schmersal-gray-dark">
        <div className="flex-shrink-0 px-6 py-5 border-b-2 border-schmersal-gray-light dark:border-schmersal-gray-dark bg-schmersal-blue">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0">
            <h3 className="text-lg font-bold text-white">
              {t('dashboard.tabela.titulo', {
                total: filtroDocumentoAtivo ? ativosVisiveis.length : paginacao.total,
              })}
            </h3>
            <div className="flex items-center space-x-2 w-full sm:w-auto justify-between sm:justify-start">
              <label className="text-sm text-white font-medium">{t('dashboard.tabela.itensPorPagina')}</label>
              <select
                value={paginacao.limit}
                onChange={(e) => {
                  setPaginacao(prev => ({
                    ...prev,
                    limit: parseInt(e.target.value),
                    page: 1,
                  }));
                }}
                className="border border-schmersal-blue-light rounded-lg shadow-sm 
                         focus:ring-2 focus:ring-schmersal-yellow focus:border-schmersal-yellow 
                         sm:text-sm py-1.5 px-3 bg-white dark:bg-schmersal-gray-darkest text-schmersal-blue dark:text-white font-semibold"
              >
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto min-h-0">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-schmersal-blue dark:border-white"></div>
              <p className="mt-3 text-sm text-schmersal-gray-medium dark:text-white font-medium">{t('comum.estado.carregando')}</p>
            </div>
          ) : ativosVisiveis.length === 0 ? (
            <div className="text-center py-12">
              <Package className="mx-auto h-16 w-16 text-schmersal-gray-medium dark:text-schmersal-gray-light" />
              <p className="mt-3 text-sm text-schmersal-gray-medium dark:text-white font-medium">{t('dashboard.tabela.vazio')}</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-rg-hairline dark:divide-schmersal-gray-dark">
                  <thead className="bg-schmersal-gray-light dark:bg-schmersal-gray-dark">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-schmersal-blue dark:text-white uppercase tracking-wider">
                        {t('dashboard.tabela.colunaAreaLocal')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-schmersal-blue dark:text-white uppercase tracking-wider">
                        {t('dashboard.tabela.colunaEquipamento')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-schmersal-blue dark:text-white uppercase tracking-wider">
                        {t('comum.rotulo.tipo')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-schmersal-blue dark:text-white uppercase tracking-wider">
                        {t('dashboard.tabela.colunaTag')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-schmersal-blue dark:text-white uppercase tracking-wider">
                        {t('comum.rotulo.status')}
                      </th>
                      <th className="px-3 py-4 text-left text-xs font-bold text-schmersal-blue dark:text-white uppercase tracking-wider">
                        {t('documentos.colunaDashboard')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-schmersal-blue dark:text-white uppercase tracking-wider">
                        {t('dashboard.tabela.colunaApreciacoes')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-schmersal-gray-darker divide-y divide-rg-hairline dark:divide-schmersal-gray-dark">
                    {ativosVisiveis.map((ativo) => {
                      const apreciacoes = ativo.apreciacoes || [];

                      return (
                        <tr
                          key={ativo.id}
                          role="link"
                          tabIndex={0}
                          onClick={() => abrirMaquina(ativo)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              abrirMaquina(ativo);
                            }
                          }}
                          title={t('maquinas.lista.acao.abrir')}
                          className="cursor-pointer hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark transition-colors duration-150"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-semibold text-schmersal-gray-dark dark:text-white">{ativo.area || '-'}</div>
                            <div className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">{ativo.local || '-'}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-semibold text-schmersal-gray-dark dark:text-white">{ativo.equipamento || '-'}</div>
                            {ativo.item && (
                              <div className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">{t('dashboard.tabela.item', { item: ativo.item })}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-schmersal-gray-dark dark:text-white font-medium">
                            {ativo.tipo || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-schmersal-gray-dark dark:text-white font-medium">
                            {ativo.tag || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {ativo.status ? (
                              <span className="px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full 
                                             bg-schmersal-blue dark:bg-schmersal-blue text-white">
                                {rotuloStatusAtivo(t, ativo.status)}
                              </span>
                            ) : (
                              <span className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">-</span>
                            )}
                          </td>
                          <td className="px-3 py-4">
                            <ResumoDocumentos resumo={resumoPorAtivo.get(ativo.id) ?? { total: 0, pendentes: 0, emAnalise: 0, entregues: 0, atrasados: 0, tiposPendentes: [] }} />
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <div className="space-y-1">
                              {apreciacoes.length > 0 ? (
                                <>
                                  <div className="text-xs font-semibold text-schmersal-gray-dark dark:text-white">
                                    {t('dashboard.tabela.contagemApreciacoes', { total: apreciacoes.length })}
                                  </div>
                                  {apreciacoes.slice(0, 2).map((apr) => {
                                    const temDocumento = (apr._count?.documentos || 0) > 0 || (apr.documentos?.length || 0) > 0;
                                    return (
                                      <div key={apr.id} className="flex items-center space-x-2">
                                        <span className={`inline-flex items-center text-xs font-medium ${temDocumento ? 'text-green-600 dark:text-green-400' : 'text-schmersal-gray-medium dark:text-schmersal-gray-light'
                                          }`}>
                                          {temDocumento ? (
                                            <CheckCircle className="h-4 w-4 mr-1" />
                                          ) : (
                                            <XCircle className="h-4 w-4 mr-1" />
                                          )}
                                          {t('dashboard.tabela.apreciacaoRevisao', { numero: apr.numero, revisao: apr.revisao })}
                                        </span>
                                      </div>
                                    );
                                  })}
                                  {apreciacoes.length > 2 && (
                                    <div className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
                                      {t('dashboard.tabela.maisApreciacoes', { total: apreciacoes.length - 2 })}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <span className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
                                  {t('dashboard.tabela.semApreciacoes')}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Paginação */}
        {!loading && ativosVisiveis.length > 0 && paginacao.totalPages > 1 && (
          <div className="flex-shrink-0 px-6 py-4 border-t-2 border-schmersal-gray-light dark:border-schmersal-gray-dark bg-schmersal-gray-light dark:bg-schmersal-gray-dark">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-0">
              <div className="text-sm text-schmersal-gray-dark dark:text-white font-medium text-center sm:text-left">
                {t('dashboard.paginacao.mostrando', {
                  inicio: ((paginacao.page - 1) * paginacao.limit) + 1,
                  fim: Math.min(paginacao.page * paginacao.limit, paginacao.total),
                  total: paginacao.total,
                })}
              </div>
              <div className="flex space-x-2 w-full sm:w-auto justify-center">
                <button
                  onClick={() => setPaginacao(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={paginacao.page === 1}
                  className="px-4 py-2 border border-schmersal-blue dark:border-schmersal-blue rounded-lg text-sm font-semibold 
                           text-schmersal-blue dark:text-white bg-white dark:bg-schmersal-gray-darker hover:bg-schmersal-blue hover:text-white dark:hover:bg-schmersal-blue dark:hover:text-white
                           disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {t('dashboard.paginacao.anterior')}
                </button>
                <span className="px-4 py-2 text-sm text-schmersal-gray-dark dark:text-white font-semibold flex items-center">
                  {t('dashboard.paginacao.pagina', { pagina: paginacao.page, total: paginacao.totalPages })}
                </span>
                <button
                  onClick={() => setPaginacao(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={paginacao.page >= paginacao.totalPages}
                  className="px-4 py-2 border border-schmersal-blue dark:border-schmersal-blue rounded-lg text-sm font-semibold 
                           text-schmersal-blue dark:text-white bg-white dark:bg-schmersal-gray-darker hover:bg-schmersal-blue hover:text-white dark:hover:bg-schmersal-blue dark:hover:text-white
                           disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {t('dashboard.paginacao.proxima')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
